import {PaymentDetails} from "@bounce/shared/consumer/declarations";
import {isEmpty} from "lodash";
import React, {PropsWithChildren, useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {FlexUiStateContext} from "./FlexUiStateContext";
import {IAccountFlexUIState, IFlexUIState, SaveFlexUIField} from "./types";
import {FLEX_STATE_KEY, getDefaultAccountState, getKeysPath, loadFlexUIState, maskLastFourDigits} from "./utils";

interface IProps extends PropsWithChildren {
  accountId: string;
}

const FlexUiStateProvider = ({accountId, children}: IProps) => {
  const [flexUIState, setFlexUIState] = useState<IFlexUIState>({});
  const {pathname} = useLocation();

  const isInformationPanel = pathname.includes("/backoffice/information-panel");
  const shouldStoreFlexUI = useMemo(() => {
    return isInformationPanel && !isEmpty(accountId);
  }, [accountId, isInformationPanel]);

  const saveFlexUIField: SaveFlexUIField = (key, value) => {
    if (shouldStoreFlexUI) {
      const currentAccountFlexUIState = getAccountState();
      if (!currentAccountFlexUIState) {
        return;
      }
      if (key && value !== undefined) {
        if (key === "url") {
          currentAccountFlexUIState.url = value;
        }
        const result = getKeysPath(currentAccountFlexUIState, key);
        if (result) {
          result.container[result.finalKey] = value;
          updateAccountState(currentAccountFlexUIState);
        } else {
          console.warn(`Path not found for key: ${key}`);
        }
      }
    }
  };

  const updateAccountState = (currAccountFlexUIState: IAccountFlexUIState) => {
    const currentState = {...flexUIState};
    currentState[accountId] = currAccountFlexUIState;
    setFlexUIState(currentState);
    sessionStorage.setItem(FLEX_STATE_KEY, JSON.stringify(currentState));
  };

  const saveFlexPaymentDetails = (paymentDetails: PaymentDetails) => {
    if (shouldStoreFlexUI) {
      const maskedPaymentDetails: PaymentDetails = {...paymentDetails};
      if (maskedPaymentDetails.ach) {
        const ach = {...maskedPaymentDetails.ach};
        ach.account_number = maskLastFourDigits(ach.account_number);
        ach.ABA = maskLastFourDigits(ach.ABA);
        maskedPaymentDetails.ach = ach;
      }

      if (maskedPaymentDetails.credit_card) {
        const creditCard = {...maskedPaymentDetails.credit_card};
        creditCard.card_number = maskLastFourDigits(creditCard.card_number);
        creditCard.cvv = null as any;
        maskedPaymentDetails.credit_card = creditCard;
      }

      saveFlexUIField("payment_details", maskedPaymentDetails);
    }
  };

  const getAccountState = (): IAccountFlexUIState | undefined => {
    return shouldStoreFlexUI && accountId && accountId in flexUIState ? flexUIState[accountId] : undefined;
  };

  const clearAccountFlexState = () => {
    if (shouldStoreFlexUI) {
      const currentState = {...flexUIState};
      delete currentState[accountId];
      setFlexUIState(currentState);
      sessionStorage.setItem(FLEX_STATE_KEY, JSON.stringify(currentState));
    }
  };

  const initFlexUIState = () => {
    const loadedState = loadFlexUIState();
    if (accountId && !loadedState[accountId]) {
      loadedState[accountId] = getDefaultAccountState();
      sessionStorage.setItem(FLEX_STATE_KEY, JSON.stringify(loadedState));
    }
    setFlexUIState(loadedState);
  };

  useEffect(() => {
    if (shouldStoreFlexUI) {
      initFlexUIState();
    }
  }, [shouldStoreFlexUI, accountId]);

  return (
    <FlexUiStateContext.Provider
      value={{
        saveFlexUIField,
        saveFlexPaymentDetails,
        clearAccountFlexState,
        getAccountState,
        shouldStoreFlexUI,
      }}
    >
      {children}
    </FlexUiStateContext.Provider>
  );
};

export {FlexUiStateProvider};
